export const CHECKOUT_URL = 'https://menodesk-en.impultienda.ar/checkout/'

export const IMAGES = {
  mainMockup: 'https://cdn.magicpatterns.com/uploads/5hmmQ8shTkAQaeeaq3z3kN/MENODESK-EN.png',
  author: 'https://cdn.magicpatterns.com/uploads/a1yiU3wd6iZTLSdYApapFg/Dra_Karito_real.png',
  bonus1: 'https://cdn.magicpatterns.com/uploads/gDs3RhBCvcmofwCvDefFCY/BONUS_1_MENODESK-EN.png',
  bonus2: 'https://cdn.magicpatterns.com/uploads/rNKZZcrXjrqVresUaKJZRn/BONUS_2_MENODESK-EN.png',
  bonus3: 'https://cdn.magicpatterns.com/uploads/huzGS1UxK1U23TGi3otWzg/BONUS_3_MENODESK-EN.png',
  bonus4: 'https://cdn.magicpatterns.com/uploads/gMiNswBGYo7oYXkZ2aoZtv/BONUS_4_MENODESK-EN.png',
  bonus5: 'https://cdn.magicpatterns.com/uploads/w2XuFAxEULKrxrY5VpmAXV/BONUS_5_MENODESK-EN.png',
}

export const stats = [
  { value: '6', label: 'Clinical Decision Tomes' },
  { value: '300+', label: 'Decision Tools' },
  { value: '5', label: 'Premium Bonuses' },
  { value: '5 min', label: 'To Find Your Pathway' },
]

export const pathwaySteps = ['Identify', 'Assess', 'Stratify', 'Decide', 'Adjust', 'Follow Up']

export const clinicalQuestions = [
  'How should I evaluate this patient?',
  'What risk factors should I consider?',
  'What changes my clinical pathway?',
  'How should I individualize the treatment strategy?',
  'What should I reassess if the patient isn’t responding as expected?',
  'How should I structure the follow-up?',
]

export const beforeItems = [
  'Multiple guidelines open at once',
  'Long searches for one specific answer',
  'Difficulty remembering every decision point',
  'Rebuilding your clinical reasoning with every patient',
  'Unstructured follow-up',
  'Information scattered across different resources',
]

export const afterItems = [
  'One organized clinical reference system',
  'Visual decision pathways',
  'Risk-stratification frameworks',
  'Treatment individualization tools',
  'Adjustment and follow-up pathways',
  'Ready-to-use clinical checklists',
  'Difficult-case frameworks',
  'Patient communication scripts',
]

export type Tome = {
  numeral: string
  title: string
  tagline: string
  intro: string[]
  items: string[]
  goal: string
  accent: string
}

export const tomes: Tome[] = [
  {
    numeral: 'Tome I',
    title: 'The Assessment Desk™',
    tagline: 'Start with the right clinical pathway.',
    intro: [
      'Before making a treatment decision, you need to know what you’re actually dealing with.',
      'This tome organizes the initial evaluation into a practical sequence.',
    ],
    items: [
      'Focused clinical history',
      'Symptom assessment',
      'Menopause and perimenopause evaluation',
      'Differential considerations',
      'Key clinical questions',
      'Relevant patient factors',
      'Laboratory considerations',
      'Diagnostic decision pathways',
      'Initial assessment checklists',
      'Clinical scenarios',
    ],
    goal: 'Know what to evaluate before moving to the next decision.',
    accent: '#22335A',
  },
  {
    numeral: 'Tome II',
    title: 'The Risk Desk™',
    tagline: 'Know which factors can change the pathway.',
    intro: [
      'Clinical decisions become difficult when risk factors enter the picture.',
      'This tome organizes the major risk considerations into visual decision frameworks.',
    ],
    items: [
      'Cardiovascular risk considerations',
      'Thromboembolic risk considerations',
      'Breast cancer considerations',
      'Contraindications and precautions',
      'Relevant medical history',
      'Risk-stratification pathways',
      'Safety checklists',
      'Special situations',
      'Decision points requiring further evaluation',
    ],
    goal: 'Identify the factors that can change your clinical pathway before proceeding.',
    accent: '#7A2B32',
  },
  {
    numeral: 'Tome III',
    title: 'The Treatment Decision Desk™',
    tagline: 'Move from assessment to an individualized strategy.',
    intro: [
      'Once the relevant clinical factors have been identified, the next question becomes: what should I consider for this particular patient?',
      'This tome organizes the variables involved in treatment selection and individualization.',
    ],
    items: [
      'Treatment decision pathways',
      'Hormone considerations',
      'Route considerations',
      'Dose considerations',
      'Individualization factors',
      'Patient-specific variables',
      'Benefit-risk considerations',
      'Treatment selection frameworks',
      'Clinical decision tables',
      'Practical scenarios',
    ],
    goal: 'Replace “What do I usually prescribe?” with a structured individualization process.',
    accent: '#1F4A3A',
  },
  {
    numeral: 'Tome IV',
    title: 'The Adjustment Desk™',
    tagline: 'Because starting treatment is not the end of the decision.',
    intro: [
      'What happens when symptoms persist? What if tolerability becomes an issue? What if the patient’s response isn’t what you expected?',
      'This tome provides structured pathways for reassessment and adjustment.',
    ],
    items: [
      'Follow-up decision pathways',
      'Response assessment',
      'Persistent symptoms',
      'Tolerability considerations',
      'Adjustment frameworks',
      'When to reassess',
      'When to consider changing the strategy',
      'Monitoring checklists',
      'Follow-up scenarios',
      'Clinical review templates',
    ],
    goal: 'Know what to reassess when the initial plan doesn’t produce the expected outcome.',
    accent: '#8A5321',
  },
  {
    numeral: 'Tome V',
    title: 'The Complex Case Desk™',
    tagline: 'For the patients who don’t fit the standard scenario.',
    intro: [
      'Some consultations are simple. Others require considerably more clinical reasoning.',
      'This tome organizes difficult and special scenarios into structured pathways.',
    ],
    items: [
      'Complex patient scenarios',
      'Special populations',
      'Relevant comorbidities',
      'Contraindications and precautions',
      'Difficult decision pathways',
      'Non-hormonal alternatives',
      'Situations requiring additional evaluation',
      'Clinical case frameworks',
      'Common decision traps',
      'Practical decision maps',
    ],
    goal: 'Have a structured starting point when the patient doesn’t fit the standard pathway.',
    accent: '#5B3F6B',
  },
  {
    numeral: 'Tome VI',
    title: 'The Consultation Desk™',
    tagline: 'Turn your clinical reasoning into a repeatable consultation workflow.',
    intro: [
      'The final piece isn’t just knowing what to consider. It’s being able to organize the consultation.',
    ],
    items: [
      'Pre-consultation checklists',
      'Consultation checklists',
      'Clinical documentation templates',
      'Follow-up templates',
      'Risk-benefit documentation',
      'Patient discussion frameworks',
      'Communication scripts',
      'Treatment-plan worksheets',
      'Monitoring sheets',
      'Consultation workflow',
    ],
    goal: 'Create a more structured consultation process from first assessment to follow-up.',
    accent: '#14304F',
  },
]

export type Bonus = {
  label: string
  title: string
  hook: string
  description: string
  items?: string[]
  value: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    label: 'Bonus #1',
    title: 'Menopause Quick Reference™',
    hook: 'Your rapid-reference companion.',
    description:
      'A condensed collection of key tables, decision points and clinical reminders designed for quick consultation.',
    value: '$19',
    image: IMAGES.bonus1,
  },
  {
    label: 'Bonus #2',
    title: 'Menopause Lab Decoder™',
    hook: 'Stop over-ordering and start interpreting with structure.',
    description:
      'A practical reference for organizing commonly encountered laboratory questions and understanding when laboratory information may or may not influence the clinical pathway.',
    items: [
      'FSH reference framework',
      'Estradiol considerations',
      'LH considerations',
      'Laboratory decision points',
      'Common interpretation questions',
      'Quick-reference tables',
    ],
    value: '$25',
    image: IMAGES.bonus2,
  },
  {
    label: 'Bonus #3',
    title: 'Patient Conversation Scripts™',
    hook: 'Know how to structure difficult conversations.',
    description: 'Practical scripts and conversation frameworks for questions about:',
    items: [
      'Hormone therapy',
      'Cancer concerns',
      'Thrombotic risk',
      'Benefits and risks',
      'Expectations',
      'Adherence',
      'Follow-up',
    ],
    value: '$29',
    image: IMAGES.bonus3,
  },
  {
    label: 'Bonus #4',
    title: 'Complication Decision Cards™',
    hook: 'What should you consider when something doesn’t go as planned?',
    description: 'Visual decision cards covering common follow-up and complication scenarios.',
    items: [
      'Persistent symptoms',
      'Side effects',
      'Bleeding considerations',
      'Treatment adjustments',
      'Reassessment pathways',
      'When additional evaluation may be appropriate',
    ],
    value: '$35',
    image: IMAGES.bonus4,
  },
  {
    label: 'Bonus #5',
    title: 'Professional Consultation Kit™',
    hook: 'Everything you need to structure your consultation workflow.',
    description: 'Includes:',
    items: [
      'Pre-treatment checklist',
      'Consultation checklist',
      'Follow-up checklist',
      'Clinical notes template',
      'Risk-benefit documentation framework',
      'Treatment-plan worksheet',
      'Follow-up record',
    ],
    value: '$39',
    image: IMAGES.bonus5,
  },
]

export const valueRows = [
  { name: 'Menopause Decision Desk™ (6 Tomes)', value: '$197' },
  { name: 'Menopause Quick Reference™', value: '$19' },
  { name: 'Menopause Lab Decoder™', value: '$25' },
  { name: 'Patient Conversation Scripts™', value: '$29' },
  { name: 'Complication Decision Cards™', value: '$35' },
  { name: 'Professional Consultation Kit™', value: '$39' },
]

export const audienceItems = [
  'Regularly encounter menopause-related consultations',
  'Want a faster way to navigate clinical information',
  'Prefer visual decision tools over long-form references',
  'Want structured consultation workflows',
  'Need practical checklists and templates',
  'Encounter complex or individualized cases',
  'Want a centralized reference system',
]

export type Testimonial = {
  name: string
  role: string
  quote: string
  image: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Dr. Elena Rodríguez',
    role: 'OB-GYN · Madrid, Spain',
    quote:
      'I used to open three guidelines to answer one question. Now I open the pathway that matches the scenario in front of me and keep moving. It changed how I run the consultation.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/9db3c547-92df-42d6-8489-ed7c44bd07b0.jpg',
  },
  {
    name: 'Dr. Laura Feldman',
    role: 'Family Medicine · Toronto, Canada',
    quote:
      'The risk-stratification frameworks are what I reach for most. Having the decision points laid out visually means I stop second-guessing what I might be missing.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/3806a3ff-4d31-44eb-8744-72368acc682e.jpg',
  },
  {
    name: 'Dr. Martín Alcázar',
    role: 'Endocrinology · Buenos Aires, Argentina',
    quote:
      'The adjustment pathways solved my real problem: what to reassess when the first plan doesn’t deliver. Structured, fast, and genuinely usable mid-consultation.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/cf018cb7-bcbf-4b48-9e5e-21af3565b55d.jpg',
  },
  {
    name: 'Amara Bennett, NP',
    role: 'Nurse Practitioner · Atlanta, USA',
    quote:
      'The conversation scripts alone were worth it. My patients leave the room understanding the decision we made together — and my notes are far more complete.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/854abf1b-97c6-44ee-9fcf-a565e92f076e.jpg',
  },
  {
    name: 'Dr. Mei Tanaka',
    role: 'Gynecology · Lima, Peru',
    quote:
      'I keep the Consultation Desk open on a second screen. Pre-consultation checklist, documentation, follow-up template — the whole visit has a structure now.',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/18ca664d-e15c-4554-ba01-9571e3820ad1.jpg',
  },
]

export const faqs = [
  {
    q: 'Is Menopause Decision Desk™ an ebook?',
    a: 'No. Although the six tomes are delivered digitally, the product is structured as a clinical decision system, with visual pathways, algorithms, checklists, tables, cases and practical consultation tools.',
  },
  {
    q: 'Do I need to read all six tomes?',
    a: 'No. They are designed to be used as reference tools. You can go directly to the tome that corresponds to the clinical stage or question you are working through.',
  },
  {
    q: 'Is this a substitute for clinical judgment?',
    a: 'No. It is an educational and reference resource designed to help organize clinical information. Healthcare professionals remain responsible for individualized assessment, diagnosis, treatment decisions and patient care.',
  },
  {
    q: 'Is the information evidence-based?',
    a: 'The material should be built and maintained using current clinical guidelines and scientific references, with the relevant sources identified within the product. Always verify recommendations against the most current applicable guidelines and local requirements.',
  },
  {
    q: 'Can healthcare professionals outside the United States use it?',
    a: 'The system can be useful as an educational reference, but clinical recommendations, regulations, approved therapies and local guidelines can vary by country. Always apply the material within your local clinical and regulatory context.',
  },
  {
    q: 'Is it a subscription?',
    a: 'No. The current offer is a one-time payment of $27 USD.',
  },
  {
    q: 'How do I receive the product?',
    a: 'After payment confirmation, you’ll receive digital access according to the delivery method used for the offer.',
  },
  {
    q: 'How long do I have access?',
    a: 'The offer provides lifetime digital access, subject to the applicable product terms.',
  },
]

export const purchaseNotifications = [
  { name: 'Dra. Valentina Ríos', place: 'Bogotá, Colombia' },
  { name: 'Dr. Andrés Beltrán', place: 'Ciudad de México, México' },
  { name: 'Dra. Camila Fuentes', place: 'Santiago, Chile' },
  { name: 'Dr. Joaquín Herrera', place: 'Buenos Aires, Argentina' },
  { name: 'Dra. Lucía Márquez', place: 'Madrid, España' },
  { name: 'Dra. Paula Rivas', place: 'Lima, Perú' },
  { name: 'Dr. Sebastián Cruz', place: 'Quito, Ecuador' },
  { name: 'Dra. Mariana Solís', place: 'Montevideo, Uruguay' },
  { name: 'Dr. Emilio Navarro', place: 'San José, Costa Rica' },
  { name: 'Dra. Isabela Duarte', place: 'Asunción, Paraguay' },
  { name: 'Dra. Renata Ocampo', place: 'Guadalajara, México' },
  { name: 'Dr. Tomás Aguirre', place: 'Barcelona, España' },
]
